import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import { DeleteModal, ProfileDetails } from "../v3";
import { HumanResourceService } from "../../services/hr.service";
import { EDIT_WORKER, DELETE_WORKER, VIEW_WORKER_WELFARE } from "../../constants";
import { AddEditWorker } from "./AddEditWorker.component";
import { formatBalance, getBalanceColor, useHasPermission } from "../../utils";
import { WORKER_AVATAR } from "../../utils/image.constants";

export const WorkerDetails = ({ onSuccess = () => {}, reloadData }) => {
  const [workerDetails, setWorkerDetails] = useState({});
  const [welfareCollected, setWelfareCollected] = useState(0);
  const [isActive, setIsActive] = useState(false);
  const [isGroupWorker, setIsGroupWorker] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    workerId: null,
  });

  const { id } = useParams();
  const navigate = useNavigate();

  const hasWorkerEdit = useHasPermission(EDIT_WORKER);
  const hasWorkerDelete = useHasPermission(DELETE_WORKER);
  const canViewWelfare = useHasPermission(VIEW_WORKER_WELFARE);

  useEffect(() => {
    fetchData();
  }, [id, reloadData]);

  const fetchData = async () => {
    try {
      const [{ data }, welfareRes] = await Promise.all([
        HumanResourceService.getEmployeeById(id),
        canViewWelfare
          ? HumanResourceService.getWorkerWelfareTotal(id).catch(() => ({ data: { totalWelfare: 0 } }))
          : Promise.resolve({ data: { totalWelfare: 0 } }),
      ]);
      setWorkerDetails(data);
      setWelfareCollected(welfareRes?.data?.totalWelfare || 0);
      setIsActive(data?.active);
      setIsGroupWorker(Boolean(data?.isGroupWorker));
      onSuccess(data?.name, Boolean(data?.isGroupWorker));
    } catch (error) {
      toast.error("Failed to fetch worker details");
      console.error("🚀 ~ fetchData ~ error:", error);
    }
  };

  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => setOpenModal(false);

  const openDeleteModal = () => {
    setDeleteModal({
      isOpen: true,
      workerId: id,
    });
  };

  const closeDeleteModal = () => {
    setDeleteModal({
      isOpen: false,
      workerId: null,
    });
  };

  const handleDelete = async () => {
    try {
      await HumanResourceService.deleteEmployee(deleteModal.workerId);
      toast.success("Worker deleted successfully");
      closeDeleteModal();
      navigate(-1);
    } catch (error) {
      toast.error("Failed to delete worker");
      console.error("🚀 ~ deleteWorker ~ error:", error);
    }
  };

  const handleStatusChange = async (e) => {
    const updatedStatus = e.target.checked;
    try {
      await HumanResourceService.updateEmployee(id, { active: updatedStatus });
      setIsActive(updatedStatus);
      toast.success("Status updated successfully");
    } catch (error) {
      toast.error("Failed to update status");
      console.error("🚀 ~ onActiveClick ~ error:", error);
    }
  };

  return (
    <>
      <ProfileDetails
        profileImage={WORKER_AVATAR}
        name={workerDetails?.name}
        subtitle={workerDetails?.designation}
        phone={workerDetails?.primaryPhone ? workerDetails.primaryPhone : "not available"}
        isActive={isActive}
        canToggle={hasWorkerEdit}
        canEdit={hasWorkerEdit}
        canDelete={hasWorkerDelete}
        onEdit={handleOpenModal}
        onDelete={openDeleteModal}
        onToggle={(e) => handleStatusChange(e)}
        infoFields={[
          {
            label: "Balance",
            value: formatBalance(workerDetails?.account?.balance),
            color: getBalanceColor(workerDetails?.account?.balance),
          },
          { label: "Unit Charge", value: workerDetails?.salary ? `₹ ${workerDetails.salary}` : 0 },
          { label: "Over Time Charge", value: workerDetails?.overtimeHourlyRate ? `₹ ${workerDetails?.overtimeHourlyRate}` : 0 },
          { label: "Aadhaar Number", value: workerDetails?.aadhaarNumber ? workerDetails?.aadhaarNumber : "-" },
          { label: "Group Worker", value: isGroupWorker ? "Yes" : "No" },
          ...(canViewWelfare
            ? [
                {
                  label: "Welfare Amount",
                  value: workerDetails?.welfareAmount ? `₹ ${Number(workerDetails.welfareAmount).toLocaleString("en-IN")}` : "—",
                },
                {
                  label: "Welfare Collected",
                  value: welfareCollected ? `₹ ${Number(welfareCollected).toLocaleString("en-IN")}` : "—",
                },
              ]
            : []),
        ]}
      />

      {/* edit worker modal */}
      <AddEditWorker workerId={id} open={openModal} onClose={handleCloseModal} onSuccess={fetchData} />

      {/* Delete Confirmation Modal */}
      <DeleteModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        message="Are you sure you want to delete this worker?"
      />
    </>
  );
};
